import React from 'react';
import { ShieldCheck, UserCheck, Stethoscope, Award, Lock, Sparkles } from 'lucide-react';
import { SpecialistAvatar } from './SpecialistAvatar';

interface DoctorTrustSectionProps {
  onOpenLeadModal: (title?: string) => void;
}

export const DoctorTrustSection: React.FC<DoctorTrustSectionProps> = ({ onOpenLeadModal }) => {
  const trustPoints = [
    {
      icon: <Stethoscope className="w-6 h-6 text-amber-600" />,
      title: 'Hekim Kontrolü & Medikal Protokol',
      description: 'Lazer epilasyon yüksek enerjili bir işlemdir. Cilt tipinize ve kıl kökü derinliğinize uygun güvenli dozaj hekim gözetiminde belirlenir; yanık veya leke riski sıfıra indirilir.'
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-amber-600" />,
      title: 'Ben (Nevüs) ve Dövme Koruması',
      description: 'Vücuttaki benlerin üzerine doğrudan lazer atışı yapılmaz. Kliniğimizde tüm benleriniz ve dövmeleriniz medikal koruyucu bariyerlerle kapatılarak güvenceye alınır.'
    },
    {
      icon: <Lock className="w-6 h-6 text-amber-600" />,
      title: 'Erkeklere Özel Seans Odası & %100 Gizlilik',
      description: 'Kadın ve erkek seans süreçleri birbirinden tamamen bağımsızdır. Özel erkek seans odamızda maksimum konfor ve gizlilik standartları uygulanır.'
    },
    {
      icon: <Award className="w-6 h-6 text-amber-600" />,
      title: 'FDA & CE Medikal Onaylı Cihaz Parkı',
      description: 'Merdivenaltı replika veya zayıf IPL cihazlar yerine; erkek kıl kökünün derin anatomisine nüfuz eden medikal sınıf hibrit buz başlık teknolojisi kullanılır.'
    },
    {
      icon: <UserCheck className="w-6 h-6 text-amber-600" />,
      title: 'Ücretsiz Ön Muayene & Kıl Analizi',
      description: 'Hiçbir ücret ödemeden FSM Bulvarı kliniğimizde kıl yapınız incelenir, tahmini seans sayınız ve size özel en avantajlı paket planlaması yapılır.'
    },
    {
      icon: <Sparkles className="w-6 h-6 text-amber-600" />,
      title: 'En Üst Düzey Hijyen & Sterilizasyon',
      description: 'Her seansta tek kullanımlık medikal sedye örtüleri, dezenfekte edilen safir başlıklar ve steril ortam koşulları sağlanır.'
    }
  ];

  return (
    <section id="doktor-guvencesi" className="py-12 sm:py-16 bg-white border-t border-slate-200 relative">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16">
        
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-300 text-amber-900 text-xs font-bold shadow-2xs">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
            <span>Neden Merdivenaltı Salon Değil, Klinik?</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Bursa FSM Bulvarı'nda <span className="text-amber-600">Doktor Kliniği Güvencesi</span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            Erkek lazer epilasyonunda kalıcı başarı; doğru dalga boyu, medikal hijyen ve cilt sağlığını koruyan uzman bir yaklaşım ile mümkündür.
          </p>
        </div>

        {/* 6 Grid Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-8">
          {trustPoints.map((point, index) => (
            <div
              key={index}
              className="bg-slate-50/80 border border-slate-200 rounded-2xl p-4.5 sm:p-5 hover:bg-white hover:border-amber-300 hover:shadow-md transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-3 mb-2.5">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    {point.icon}
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                    {point.title}
                  </h3>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {point.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Doctor Consultation CTA Banner with Specialist Avatar */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-950 rounded-2xl border border-slate-700 p-5 sm:p-6 flex flex-col md:flex-row items-center justify-between gap-5 shadow-lg text-white">
          <div className="flex items-center gap-4 text-left">
            <SpecialistAvatar size="lg" showOnlineStatus={true} />
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-black text-white">
                  Uzmanımız ile Birebir Ön Değerlendirme
                </h3>
                <span className="text-[10px] font-bold text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded border border-amber-400/30">
                  Uzman Estetisyen
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-400/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Şu An Çevrim İçi
                </span>
              </div>
              <p className="text-xs text-slate-300 max-w-xl">
                FSM Bulvarı kliniğimizde görev yapan uzman estetisyenimiz ile kıl köklerinizi inceleyelim; cilt renginize, batık durumunuza ve hedefinize özel seans takviminizi birlikte planlayalım.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2.5 shrink-0 w-full md:w-auto">
            <button
              onClick={() => onOpenLeadModal('Doktor Kliniği Ücretsiz Kıl Muayenesi')}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold text-xs border border-slate-600 transition-all shadow-2xs cursor-pointer"
            >
              Ön Randevu Talebi
            </button>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== 'undefined' && window.openLiveChat) {
                  window.openLiveChat();
                } else if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('open-live-chat'));
                }
              }}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-2xs hover:brightness-105 transition-all cursor-pointer"
            >
              <SpecialistAvatar size="xs" showOnlineStatus={false} bordered={false} />
              <span>Uzman İle Canlı Görüşün</span>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
};
